import { connect } from 'react-redux';
import { routeTo } from 'redux-router-kit';

import * as AppAdmin from 'app/entities/AppAdmin';
import * as CliApp from 'app/entities/CliApp';
import * as CliAppDefinition from 'app/entities/CliAppDefinition';
import * as DeveloperActions from 'app/developer-v3/Actions';
import LoadingBlockOrComponent from 'app/common/components/LoadingBlockOrComponent';
import MaybeRedirect from 'app/common/components/MaybeRedirect';
import hocify from 'app/common/components/hocify';
import { actions as ManageAppActions } from 'app/developer-v3/store/manage';
import { notify } from 'app/developer-v3/store/shared/actions';
import {
  currentAppDefinitionEntity,
  currentAppEntity,
  currentAppId,
  currentAppVersion,
  currentAppVersionEntities,
  currentDefinition,
  currentDefinitionOverride,
  isCurrentAppIntentionPublic,
  isNotAdmin,
  isStaff,
} from 'app/developer-v3/selectors';
import {
  getAppUrl,
  getAppVersionUrl,
  getVersionStatus,
  isAdminOnlyRoute,
  isGUIIntegrationOnlyRoute,
  isPublicIntentionOnlyRoute,
  parseBackendAppToAppFormType,
  sortVersionStrings,
} from 'app/developer-v3/utils';

import AppBuilder from './AppBuilder';
import { SIDEBAR_VERSION_DROPDOWN_VALUES } from '../Sidebar';
import { hasMissingRequiredIntegrationSettings } from './utils';

const mapStateToProps = (state, props) => {
  const id = currentAppId(state);
  const version = currentAppVersion(state);

  const appEntity = currentAppEntity(state);
  const appDefinitionEntity = currentAppDefinitionEntity(state);

  const definition = currentDefinition(state);
  const definitionOverride = currentDefinitionOverride(state);
  const isLoading =
    CliApp.selectors.isLoadImpending(state) ||
    CliAppDefinition.selectors.isLoadImpending(state) ||
    AppAdmin.selectors.isLoadImpending(state);
  const versionEntities = currentAppVersionEntities(state);
  const versionEntity = versionEntities.find(
    entity => CliAppDefinition.selectors.version(entity) === version
  );
  const isGUIIntegration =
    !isLoading &&
    CliAppDefinition.selectors.isGUIIntegration(appDefinitionEntity) === true;
  const isCLIIntegration =
    !isLoading &&
    CliAppDefinition.selectors.isGUIIntegration(appDefinitionEntity) === false;
  /**
   * Checks if the API returned a status code of 5xx
   *
   * Used to show the user an error explaining that zapier.com API can be down
   */
  const hasFailure =
    (CliAppDefinition.selectors.all.isFailure(state) ||
      CliApp.selectors.all.isFailure(state)) &&
    (CliAppDefinition.selectors.isAPIDown(appDefinitionEntity) ||
      CliApp.selectors.isAPIDown(appEntity));

  const userCount = CliAppDefinition.selectors.userCount(appDefinitionEntity);

  const isGUIOnlyRoute = isGUIIntegrationOnlyRoute(props.router);

  const integrationSettings = appEntity?.data
    ? parseBackendAppToAppFormType(appEntity.data)
    : undefined;

  return {
    appSummary: {
      appId: id,
      definition,
      definitionOverride,
      description: CliApp.selectors.description(appEntity) || '',
      imageUrl: CliApp.selectors.imageUrl(appEntity),
      isBeta: CliApp.selectors.isBeta(appEntity),
      isGUIIntegration,
      isPending: CliApp.selectors.isPending(appEntity),
      isPrivate: CliApp.selectors.isPrivate(appEntity),
      isPublic: CliApp.selectors.isPublic(appEntity),
      isPublished: CliApp.selectors.isPublished(appEntity),
      key: CliApp.selectors.key(appEntity) || '',
      platformVersion: CliAppDefinition.selectors.platformVersion(
        versionEntity
      ),
      slug: CliApp.selectors.slug(appEntity),
      status: CliApp.selectors.status(appEntity),
      title: CliApp.selectors.title(appEntity) || 'App Name',
      version,
      users: {
        count: userCount,
        exceedLimit: CliAppDefinition.selectors.doesUserCountExceedLimit(
          appDefinitionEntity
        ),
      },
      versionStatus: getVersionStatus(
        CliAppDefinition.selectors.deployment(versionEntity),
        CliAppDefinition.selectors.deprecationDate(versionEntity)
      ),
      versions: sortVersionStrings(CliApp.selectors.activeVersions(appEntity)),
      wasConvertedFromWB: CliApp.selectors.wasConvertedFromWB(appEntity),
    },
    hasMissingRequiredIntegrationSettings:
      integrationSettings &&
      hasMissingRequiredIntegrationSettings(integrationSettings),
    hasFailure,
    isAppFound: !CliApp.selectors.isNotFound(appEntity),
    isAppLoading: isLoading,
    isLoading,
    redirectUrl: getAppVersionUrl(id, version),
    shouldRedirect:
      (!isStaff && isCLIIntegration && isGUIOnlyRoute) ||
      (isNotAdmin(state) && isAdminOnlyRoute(props.router)) ||
      (!isCurrentAppIntentionPublic(state) &&
        isPublicIntentionOnlyRoute(props.router)),
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  async onLoad() {
    const {
      params: { id, version },
    } = props;
    await this.refreshVersionList();
    this.resetAppStatus();
    await dispatch([
      DeveloperActions.loadAppAndVersion(id, version),
      AppAdmin.actions.resetAndLoadCollection(),
    ]);
    if (version) {
      dispatch(DeveloperActions.loadEnvironmentForVersion());
    }
  },
  refreshVersionList() {
    const {
      params: { id },
    } = props;
    return dispatch(DeveloperActions.loadAppAndAllVersions(id));
  },
  resetAppStatus() {
    return dispatch(ManageAppActions.resetCurrentAppStatus());
  },
  async onVersionChange(versionNumber: string) {
    const {
      params: { id, version },
    } = props;
    if (versionNumber.startsWith(`${SIDEBAR_VERSION_DROPDOWN_VALUES.CLONE}:`)) {
      // TODO: Ask for version numbers
      const [fromVersionNumber, toVersionNumber] = versionNumber
        .replace(`${SIDEBAR_VERSION_DROPDOWN_VALUES.CLONE}:`, '')
        .split(';');
      versionNumber = toVersionNumber;
      dispatch(
        notify.info(`Cloning ${fromVersionNumber} to ${toVersionNumber}...`)
      );
      await dispatch(
        DeveloperActions.cloneAppVersion(id, fromVersionNumber, toVersionNumber)
      );
    } else if (
      versionNumber.startsWith(`${SIDEBAR_VERSION_DROPDOWN_VALUES.MIGRATE}:`)
    ) {
      // TODO: Ask for version numbers and percentage or email
      const [fromVersionNumber, toVersionNumber] = versionNumber
        .replace(`${SIDEBAR_VERSION_DROPDOWN_VALUES.MIGRATE}:`, '')
        .split(';');
      versionNumber = toVersionNumber;
      dispatch(
        notify.info(`Migrating ${fromVersionNumber} to ${toVersionNumber}...`)
      );
      await dispatch(
        DeveloperActions.migrateAppVersion(
          id,
          fromVersionNumber,
          toVersionNumber,
          { percentage: 100 }
        )
      );
    } else if (versionNumber === SIDEBAR_VERSION_DROPDOWN_VALUES.PROMOTE) {
      // TODO: Ask for changelog
      versionNumber = version;
      dispatch(notify.info(`Promoting ${versionNumber}...`));
      await dispatch(DeveloperActions.promoteAppVersion(id, versionNumber));
    } else if (
      versionNumber.startsWith(`${SIDEBAR_VERSION_DROPDOWN_VALUES.DEPRECATE}:`)
    ) {
      // TODO: Ask for date
      const date = versionNumber.replace(
        `${SIDEBAR_VERSION_DROPDOWN_VALUES.DEPRECATE}:`,
        ''
      );
      versionNumber = version;
      dispatch(notify.info(`Deprecating ${versionNumber}...`));
      await dispatch(
        DeveloperActions.deprecateAppVersion(id, versionNumber, date)
      );
    }

    dispatch(
      versionNumber === SIDEBAR_VERSION_DROPDOWN_VALUES.ALL
        ? routeTo(`${getAppUrl(id)}/versions`)
        : DeveloperActions.viewAppVersion(versionNumber, undefined)
    );
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(hocify(LoadingBlockOrComponent)(hocify(MaybeRedirect)(AppBuilder)));
